/**
 * Partner Distributions View — FIE Screen 2
 * Trust-first, progressive disclosure. No raw forecast access.
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Lock, ShieldAlert, ChevronDown, ChevronRight } from "lucide-react";
import { useFIE } from "@/data/fie/context";
import { ConfidenceBadge, Currency, RangeDisplay, ConfidenceDetail } from "./SharedComponents";
import { InlineAttributionPanel } from "./InlineAttributionPanel";
import { useRole } from "@/data/RoleContext";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import type { PartnerDistribution } from "@/data/fie/types";

export function PartnerDistributionsView() {
  const {
    visibleDistributions, visiblePartners, visibleForecast,
    canViewAllPartners, canViewOwnOnly,
    logDistributionViewed,
  } = useFIE();
  const { role } = useRole();

  useEffect(() => {
    if (canViewOwnOnly && visibleDistributions.length > 0) {
      logDistributionViewed(visibleDistributions[0].partnerId, `U-${role.toUpperCase()}`, role);
    }
  }, [canViewOwnOnly, role]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-lg font-bold tracking-tight">Partner Distributions</h2>
        <p className="text-xs text-muted-foreground">
          {canViewAllPartners
            ? "Projected earnings by partner based on ownership and forecasted distributable amount"
            : "Your projected earnings based on ownership and forecasted distributable amount"
          }
        </p>
      </div>

      {/* Distributable Summary — only for full-access */}
      {canViewAllPartners && visibleForecast.distributableAmount && (
        <Card className="border-2 border-primary/10">
          <CardContent className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <RangeDisplay range={visibleForecast.distributableAmount} label="Total Distributable Amount" />
              <div className="space-y-1">
                <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">Partners</p>
                <p className="text-2xl font-bold">{visiblePartners.length}</p>
                {visibleForecast.revenueLines && (
                  <p className="text-xs text-muted-foreground">{visibleForecast.revenueLines.length} contributing jobs</p>
                )}
              </div>
              <ConfidenceDetail metadata={visibleForecast.confidence} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Permission Notice */}
      {canViewOwnOnly && (
        <div className="flex items-center gap-2 p-3 rounded-lg border bg-warning/5 border-warning/20">
          <ShieldAlert className="h-4 w-4 text-warning" />
          <p className="text-sm text-warning">You are viewing your own projected distribution only. Company-wide totals and other partner data are restricted.</p>
        </div>
      )}
      {!canViewAllPartners && !canViewOwnOnly && (
        <div className="flex items-center gap-2 p-3 rounded-lg border bg-warning/5 border-warning/20">
          <Lock className="h-4 w-4 text-warning" />
          <p className="text-sm text-warning">You are viewing limited distribution data based on your access level.</p>
        </div>
      )}

      {/* Distribution Cards with progressive disclosure */}
      <div className={cn("grid grid-cols-1 gap-4", canViewAllPartners && "md:grid-cols-2")}>
        {visibleDistributions.map(d => (
          <DistributionCard
            key={d.partnerId}
            distribution={d}
            canViewAll={canViewAllPartners}
            canViewOwn={canViewOwnOnly}
          />
        ))}
      </div>

      {/* Ownership Table */}
      {canViewAllPartners && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Ownership Registry</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Partner</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Ownership %</TableHead>
                  <TableHead>Effective Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visiblePartners.map(p => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell><Badge variant="outline">{p.entity}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.role}</TableCell>
                    <TableCell className="text-right font-bold">{p.ownershipPercentage}%</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(p.effectiveDate).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Distribution Card with inline breakdown ──
function DistributionCard({ distribution: d, canViewAll, canViewOwn }: {
  distribution: PartnerDistribution; canViewAll: boolean; canViewOwn: boolean;
}) {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <Card className="border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">{canViewAll ? d.partnerName : "Your Distribution"}</CardTitle>
          </div>
          {(canViewAll || canViewOwn) && (
            <Badge variant="outline">{d.ownershipPercentage}% Ownership</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <RangeDisplay range={d.projectedPayout} label="Projected Payout" />

        {/* Data coverage bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Actual Data Coverage</span>
            <span className="font-medium">{d.actualInputPercentage}%</span>
          </div>
          <Progress value={d.actualInputPercentage} className="h-1.5" />
        </div>

        {/* Unresolved items */}
        {d.lineage.unresolvedItems && d.lineage.unresolvedItems.length > 0 && (
          <div className="p-2 rounded border bg-warning/5 border-warning/20">
            <p className="text-[10px] font-medium uppercase tracking-wider text-warning mb-1">Unresolved Items</p>
            <ul className="text-xs text-warning space-y-0.5">
              {(canViewOwn
                ? ["Some data inputs are estimated — see confidence score"]
                : d.lineage.unresolvedItems.slice(0, 3)
              ).map((item, i) => (
                <li key={i}>• {item}</li>
              ))}
              {!canViewOwn && d.lineage.unresolvedItems.length > 3 && (
                <li className="text-warning">+ {d.lineage.unresolvedItems.length - 3} more</li>
              )}
            </ul>
          </div>
        )}

        {/* Bottom bar: confidence + expand */}
        <div className="flex items-center justify-between pt-1">
          <ConfidenceBadge level={d.confidence.level} score={d.confidence.score} />
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {showDetails ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Details
          </button>
        </div>

        {/* Expanded details — inline, not modal */}
        {showDetails && (
          <div className="space-y-4 pt-3 border-t animate-in slide-in-from-top-1 duration-200">
            {/* Payout range table */}
            <div>
              <p className="text-xs text-muted-foreground mb-2">Payout Range</p>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell className="text-destructive py-1.5 text-xs">Low Case</TableCell>
                    <TableCell className="text-right py-1.5 font-medium text-sm"><Currency value={d.projectedPayout.low} /></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="py-1.5 text-xs">Expected</TableCell>
                    <TableCell className="text-right py-1.5 font-bold text-sm"><Currency value={d.projectedPayout.expected} /></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-success py-1.5 text-xs">High Case</TableCell>
                    <TableCell className="text-right py-1.5 font-medium text-sm"><Currency value={d.projectedPayout.high} /></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {canViewAll && (
              <div className="flex items-center gap-4 text-xs">
                <div>
                  <span className="text-muted-foreground">Contributing Jobs: </span>
                  <span className="font-bold">{d.contributingJobCount}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Actual Coverage: </span>
                  <span className="font-bold">{d.actualInputPercentage}%</span>
                </div>
              </div>
            )}

            {/* Assumptions */}
            {canViewAll && d.lineage.assumptionsUsed && d.lineage.assumptionsUsed.length > 0 && (
              <div>
                <p className="text-xs font-medium mb-1.5">Assumptions Impacting This Estimate:</p>
                <div className="flex flex-wrap gap-1">
                  {d.lineage.assumptionsUsed.map(a => (
                    <Badge key={a} variant="secondary" className="text-[10px] capitalize">{a}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Inline attribution breakdown */}
            {d.attributions && d.attributions.length > 0 && (
              <InlineAttributionPanel
                attributions={d.attributions}
                label={`${canViewAll ? d.partnerName : "Your"} distribution sources`}
              />
            )}

            <ConfidenceDetail metadata={d.confidence} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
